import React from 'react';
import ReactIs from 'react-is';

function parseKey(key) {
    // react attribute names use camelcase with no hyphens.
    const parts = key.split('-');
    if (parts.length > 1) {
        const newKeyParts = parts.map((c, index) => {
            if (index > 0) {
                return c.charAt(0).toUpperCase() + c.slice(1);
            }
            return c;
        });

        return newKeyParts.join('');
    }

    return key;
}

function convertStyleAttr(attr) {
    // NB: This will break if urls or data-uris are passed through the style attribute.
    // e.g.content: "data:image/svg+xml,data:text/plain;charset=utf-8,..."
    const styles = attr.split(';');

    const styleObject = {};
    if (Array.isArray(styles)) {
        styles.forEach((s) => {
            if (s) {
                const kv = s.split(':');
                const key = parseKey(kv[0]);
                const value = kv[1];

                styleObject[key] = value;
            }
        });
    }

    return styleObject;
}

function attr2Props(attributes) {
    const props = attributes.reduce((o, kv) => {
        const x = o;
        let n = kv.name;
        let v = kv.value;
        if (n === 'style') {
            v = convertStyleAttr(v);
        }
        if (n === 'class') {
            n = 'className';
        }
        // See https://reactjs.org/docs/uncontrolled-components.html#default-values
        if (n === 'value' && x.type === 'checkbox') {
            n = 'defaultChecked';
        }
        if (n === 'value' && x.type !== 'submit') {
            n = 'defaultValue';
        }
        if (n === 'checked') {
            n = 'defaultChecked';
        }
        if (n === 'readonly') {
            n = 'readOnly';
        }
        if (v === 'true') {
            v = true;
        }
        if (v === 'false') {
            v = false;
        }
        // defaultChecked and checked are bool values.
        // The only way to get an unchecked checkbox is by omitting these properties
        // Allow only 'checked' value as the 'value' attribute doesn't seem to change
        if (!(n === 'defaultChecked' && kv.value !== 'checked')) {
            x[n] = v;
        }

        return x;
    }, {});
    return props;
}

const HtmlToReact = require('html-to-react');

// Resolves and loads module content
export function requireAll(requireContext) {
    return requireContext.keys().map(requireContext);
}

// Important! This allows us to nest JSX components and HTML components inside each other
// Everything inside a <Root> element will be converted into React
export function setProcessingInstuctions(modules) {
    const processNodeDefinitions = new HtmlToReact.ProcessNodeDefinitions(React);

    const components = [];
    for (const m of modules) {
        if (m.default) { // default is the module export class
            const c = m.default;

            // https://www.npmjs.com/package/react-is
            if (ReactIs.isValidElementType(c)) {
                components.push(m.default);
            }
        }
    }

    // Order matters. Instructions are processed in
    // the order they're defined
    const processingInstructions = [];
    const doNotProcess = [];

    // Instruct the parser to replace JSX classes with their appropriate components
    for (const c of components) {
        const type = { component: c }; // This is needed to trick JSX into using the component type

        processingInstructions.push(
            {
                shouldProcessNode(node) {
                    try {
                        c.name.toLowerCase();
                    } catch {
                        return false;
                    }
                    return (node.tagName ?? '').toLowerCase() === c.name.toLowerCase() && !doNotProcess.includes(c.name.toLowerCase());
                },
                processNode(node, children, index) {
                    const props = attr2Props(node?.attributes ?? []);

                    // filter out any whitespace children
                    const filteredChildren = React.Children
                        .toArray(children)
                        .filter((child) => {
                            if (child.type || child.replace(/\s/g, '').length > 0) {
                                return child;
                            }
                            return undefined;
                        });

                    const r = (
                        <type.component
                            key={index}
                            {...props}
                        >
                            {filteredChildren}
                        </type.component>
                    );
                    return r;
                },
                // replaceChildren: true// NB: replaceChildren: true tells React to own the child nodes
            },
        );
    }

    // Add an uncontrolled state handler for pure <input> elements
    // See https://reactjs.org/docs/uncontrolled-components.html
    processingInstructions.push({
        shouldProcessNode(node) {
            return (node.tagName ?? '').toLowerCase().includes('input') && !doNotProcess.includes((node.tagName ?? '').toLowerCase());
        },
        processNode(node, children, index) {
            // eslint-disable-next-line react/destructuring-assignment
            const reactProps = attr2Props(node.attributes);
            return (
                <input
                    key={index} // All react sibling nodes must have a unique key https://reactjs.org/docs/lists-and-keys.html
                    {...reactProps}
                />
            );
        },
    });

    // Add the default case handler for a plain HTML node
    processingInstructions.push({
        shouldProcessNode(node) {
            return !doNotProcess.includes((node.tagName ?? '').toLowerCase());
        },
        processNode: processNodeDefinitions.processDefaultNode,
    });

    return processingInstructions;
}

export default { setProcessingInstuctions, requireAll };
